// src/components/InvoiceDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import InvoiceDetails from "./InvoiceDetails";
import InvoiceItemsEditor from "./InvoiceItemsEditor";
import { countries } from "../constants/countryList";
import { fetchOrders } from "../actions/performaActions";
import { useSelector, useDispatch } from "react-redux";
import { LazyLoading } from "./ui/LazyLoading";
import toast from "react-hot-toast";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { API_ERROR_LABELS, validateInvoice } from "../utils/invoiceValidation";

const BASE_CURRENCY_OPTIONS = [
  { value: "USD", label: "دلار آمریکا", aliases: ["دلار", "دلار امریکا"] },
  { value: "EUR", label: "یورو", aliases: [] },
  { value: "CNY", label: "یوان چین", aliases: ["یوان"] },
  { value: "GBP", label: "پوند استرلینگ", aliases: ["پوند"] },
  { value: "IRR", label: "ریال", aliases: [] },
  { value: "AED", label: "درهم امارات", aliases: ["درهم", "درهم امارات متحده عربی"] },
];

const resolveCurrencyValue = (currency) => {
  const normalized = String(currency || "").trim().toLocaleLowerCase();
  if (!normalized) return "";
  const option = BASE_CURRENCY_OPTIONS.find((item) =>
    [item.value, item.label, ...item.aliases].some(
      (value) => String(value).trim().toLocaleLowerCase() === normalized
    )
  );
  return option?.value || String(currency).trim();
};

export default function InvoiceDetail() {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [invoice, setInvoice] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [cottages, setCottages] = useState([]);
  const [originalCottage, setOriginalCottage] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const orders = useSelector((state) => state.order?.orders) ?? [];

  // --- Select options
  const meansOfTransportOptions = [
    { value: "By Truck", label: "By Truck" },
    { value: "By AirPlane", label: "By AirPlane" },
    { value: "By Vessel", label: "By Vessel" },
    { value: "By Train", label: "By Train" },
  ];
  const termsOfDeliveryOptions = [
    { value: "CFR", label: "CFR" },
    { value: "CIF", label: "CIF" },
    { value: "CIP", label: "CIP" },
    { value: "CPT", label: "CPT" },
    { value: "DAP", label: "DAP" },
    { value: "DDP", label: "DDP" },
    { value: "DPU", label: "DPU" },
    { value: "EXW", label: "EXW" },
    { value: "FAS", label: "FAS" },
    { value: "FCA", label: "FCA" },
    { value: "FOB", label: "FOB" },
  ];
  const unitOptions = [
    { value: "KG", label: "KG" },
    { value: "M3", label: "M3" },
    { value: "M2", label: "M2" },
    { value: "M", label: "M" },
    { value: "PCS", label: "PCS" },
    { value: "SET", label: "SET" },
  ];

  const currencyOptions = useMemo(() => {
    const options = BASE_CURRENCY_OPTIONS.map(({ aliases, ...option }) => option);
    const knownValues = new Set(options.map((option) => option.value));
    cottages.forEach((cottage) => {
      const value = resolveCurrencyValue(cottage.proforma_currency_type);
      if (value && !knownValues.has(value)) {
        options.push({ value, label: value });
        knownValues.add(value);
      }
    });
    return options;
  }, [cottages]);

  const cottageOptions = useMemo(() => {
    if (!invoice?.proforma) return [];
    return (cottages || [])
      .filter(
        (cottage) =>
          String(cottage.proforma_vcode) === String(invoice.proforma)
      )
      .map((cottage) => {
        const belongsToCurrentInvoice =
          String(cottage.cottage_number) === String(originalCottage);
        const isDisabled = Boolean(cottage.has_invoice) && !belongsToCurrentInvoice;
        return {
          value: String(cottage.cottage_number),
          label: isDisabled
            ? `${cottage.cottage_number} - دارای فاکتور`
            : String(cottage.cottage_number),
          isDisabled,
          currency: resolveCurrencyValue(cottage.proforma_currency_type),
        };
      });
  }, [cottages, invoice?.proforma, originalCottage]);

  const proformaOptions = (orders || []).map((o) => ({
    value: String(o.prfVCodeInt),
    label: String(o.prf_order_no),
  }));

  const sellerOptions = (sellers || []).map((sel) => ({
    value: sel.id,
    label: sel.seller_name,
  }));

  const countryOptions = (countries || []).map((country) => ({
    value: country.name,
    label: `${country.name} (${country.persianName})`,
  }));

  const buyerOptions = (buyers || []).map((buyer) => ({
    value: buyer.id,
    label: buyer.buyer_name,
  }));

  // --- Fetch data
  useEffect(() => {
    dispatch(fetchOrders(1, 600));
    const fetchData = async () => {
      try {
        const [invoiceRes, sellersRes, buyersRes, cottagesRes] =
          await Promise.all([
            axiosInstance.get(
              `/documents/invoices/by-number/${invoiceNumber}/`
            ),
            axiosInstance.get("/documents/sellers/"),
            axiosInstance.get("/documents/buyers/"),
            axiosInstance.get("cottages/numbers/", { params: { page_size: 600 } }),
          ]);

        setInvoice(invoiceRes.data);
        setOriginalCottage(String(invoiceRes.data.cottage || ""));
        setSellers(sellersRes.data);
        setBuyers(buyersRes.data);

        const list = Array.isArray(cottagesRes.data)
          ? cottagesRes.data
          : Array.isArray(cottagesRes.data?.results)
          ? cottagesRes.data.results
          : [];
        setCottages(list);

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("دریافت اطلاعات فاکتور ناموفق بود.");
        toastApiError(err, {}, "دریافت اطلاعات فاکتور ناموفق بود.");
        setLoading(false);
      }
    };
    fetchData();
  }, [invoiceNumber, dispatch]);

  // --- Field handlers
  const handleInvoiceFieldChange = (field, value) => {
    if (field === "proforma") {
      setInvoice((prev) => ({
        ...prev,
        proforma: value,
        cottage: "",
        invoice_currency: "",
      }));
      return;
    }
    if (field === "cottage") {
      const selected = cottageOptions.find(
        (option) => String(option.value) === String(value)
      );
      if (selected?.isDisabled) {
        toast.error("برای این کوتاژ قبلاً فاکتور ثبت شده است.");
        return;
      }
      setInvoice((prev) => ({
        ...prev,
        cottage: value,
        invoice_currency: selected?.currency || prev.invoice_currency,
      }));
      return;
    }
    setInvoice((prev) => ({ ...prev, [field]: value }));
  };
  const handleItemsChange = (updatedItems) => {
    setInvoice((prev) => ({ ...prev, items: updatedItems }));
  };

  // --- Submit / Delete
  const handleSubmit = async (e) => {
    e.preventDefault();
    const problems = validateInvoice({ ...invoice, items: invoice.items || [] });
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    try {
      const { data } = await axiosInstance.put(`/documents/invoices/${invoice.id}/`, {
        ...invoice,
        proforma: invoice.proforma || null,
        cottage: invoice.cottage || null,
      });
      setInvoice(data);
      setOriginalCottage(String(data.cottage || ""));
      toast.success("تغییرات با موفقیت ذخیره شد.");
    } catch (err) {
      toastApiError(err, API_ERROR_LABELS, "ذخیره تغییرات فاکتور ناموفق بود.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این فاکتور را حذف کنید؟"))
      return;

    try {
      await axiosInstance.delete(`/documents/invoices/${invoice.id}/`);
      toast.success("فاکتور با موفقیت حذف شد.");
      navigate("/invoices/list");
    } catch (err) {
      toastApiError(err, {}, "حذف فاکتور ناموفق بود.");
    }
  };

  if (loading) return <LazyLoading />;
  if (!invoice) return <div className="text-red-600">{error}</div>;

  return (
    <div dir="rtl" className="font-vazir mt-20 md:mt-24 px-5">
      {/* Top bar */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">جزئیات فاکتور</h1>
          <p className="text-gray-500 text-sm mt-1">
            شماره فاکتور:{" "}
            <span className="font-semibold">{invoice?.invoice_number}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/invoices/list"
            className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
          >
            بازگشت
          </Link>
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 rounded-xl border text-red-600 hover:bg-red-50 transition"
          >
            حذف فاکتور
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            ثبت تغییرات
          </button>
        </div>
      </div>

      {/* Details card */}
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        <div className="rounded-2xl border bg-white shadow-sm ">
          <div className="border-b px-4 md:px-6 py-4">
            <h2 className="text-base font-semibold">مشخصات فاکتور</h2>
          </div>
          <div className="px-4 md:px-6 py-6">
            <InvoiceDetails
              invoice={invoice}
              onFieldChange={handleInvoiceFieldChange}
              sellerOptions={sellerOptions}
              buyerOptions={buyerOptions}
              currencyOptions={currencyOptions}
              termsOfDeliveryOptions={termsOfDeliveryOptions}
              meansOfTransportOptions={meansOfTransportOptions}
              countryOptions={countryOptions}
              cottageOptions={cottageOptions}
              proformaOptions={proformaOptions}
            />
          </div>
        </div>

        {/* Items card */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="border-b px-4 md:px-6 py-4">
            <h2 className="text-base font-semibold">اقلام فاکتور</h2>
          </div>
          <div className="px-4 md:px-6 py-6">
            <InvoiceItemsEditor
              items={invoice.items}
              onItemsChange={handleItemsChange}
              unitOptions={unitOptions}
              countryOptions={countryOptions}
            />
          </div>
        </div>

        {/* Sticky action bar (mobile-friendly) */}
        <div className="sticky bottom-3 mt-6 z-10">
          <div className="mx-auto w-full md:w-auto md:ml-auto flex flex-col md:flex-row gap-2 md:justify-end bg-white/80 backdrop-blur rounded-2xl border p-3 shadow-sm">
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2 rounded-xl border text-red-600 hover:bg-red-50 transition"
            >
              حذف فاکتور
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              ثبت تغییرات
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
